import { useMemo } from 'react'
import { getFormattedAddress } from '@sales-center/common'
import { ToTitleCase } from '@user/utils/ToTitleCase'
import { ContactCardProps } from '../typings'
import { useTheme } from '@mui/material/styles'

export function useData(params: ContactCardProps) {
  const {
    locationType,
    isPrimaryContact,
    hideEditOptions,
    menuOptions,
    contact: {
      firstName,
      lastName,
      company,
      contactTime,
      street1,
      street2,
      city,
      stateAbbreviation,
      postalCode,
      emailAddresses,
    },
  } = params

  const contactName = useMemo(
    () => (firstName || lastName ? `${firstName} ${lastName}`.trim() : company),
    [company, firstName, lastName],
  )

  const contactTimeFormatted = useMemo(
    () =>
      contactTime &&
      contactTime
        .split(',')
        .map((x) => ToTitleCase(x))
        .join(', '),
    [contactTime],
  )

  const formattedAddress = useMemo(
    () =>
      getFormattedAddress(
        street1,
        street2,
        city,
        stateAbbreviation,
        postalCode,
      ),
    [city, postalCode, stateAbbreviation, street1, street2],
  )

  const contactTypeLabel = useMemo(() => {
    switch (locationType) {
      case 'billto':
        return 'Bill-To'
      case 'service':
        return 'Service Location'
      case 'both':
        return 'Bill-To / Service Location'
    }
  }, [locationType])

  const primaryEmail = useMemo(
    () => emailAddresses.find((x) => x.isPrimary)?.emailAddress,
    [emailAddresses],
  )
  const secondaryEmails = useMemo(
    () => emailAddresses.filter((x) => !x.isPrimary && x.emailAddress),
    [emailAddresses],
  )

  const truncatedName = (
    (firstName && firstName[0]) + (lastName && lastName[0]) || ''
  ).toUpperCase()
  const theme = useTheme()
  const cardBG = isPrimaryContact
    ? theme.palette.grey[50]
    : theme.palette.common.white

  const showMoreButton = menuOptions && !hideEditOptions

  return {
    contactName,
    contactTimeFormatted,
    formattedAddress,
    contactTypeLabel,
    primaryEmail,
    secondaryEmails,
    truncatedName,
    cardBG,
    showMoreButton,
    menuOptions,
  }
}
